import { useEffect, useRef, useCallback } from "react";
import axios from "../../utils/axiosConfig";
import { acquireCheckMicTrack, releaseSharedMicIfUnused } from "../../utils/sharedMic";
import { audioFileExtension, createAudioRecorder, recordedAudioType } from "../../utils/audioRecordingType";

const UPLOAD_INTERVAL_S_MODE = 10000; // 10s for strict real-time detection
const UPLOAD_INTERVAL_C_MODE = 10000; // 10s — keeps audio clips short for fast diarization (~1.5s)
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
// Clips being checked at once. The server answers after the speaker check, which falls behind
// when many candidates test together; a newer clip beyond this is not sent, so checks stay
// current instead of queuing (staging 26 Oct 2026: 529 uploads, 164 answers, warnings minutes late).
const MAX_IN_FLIGHT = 2;

function BackgroundAudioRecorder({
  examDetail,
  questionNo,
  MonitoringData,
  setAudioCount,
  // Reading-aloud warnings count toward Speaker Warnings too (Test.jsx decides the abort).
  setReadingAloudCount,
  setAudioAlertShowModal,
  alertVisible,
  // Which checks run for the question on screen (utils/headphonesAllowed.js audioCheckMode):
  // "skip" = no audio sent (speaking/listening questions), "speakersOnly" = a video-interview
  // answer being recorded (talking allowed, a second voice still caught), "videoQuestion" = a
  // video-interview question when not recording (no talking reminder; reading aloud and a second
  // voice still caught), "full" = everything (utils/headphonesAllowed.js).
  audioCheckMode = "full",
  // Speech alert shown by the test page: null, "talking" (reminder) or "readingAloud" (warning).
  setSpeechAlert,
  speechAlert = null,
  // qid of the question on screen: sent with each clip so the backend compares what was said
  // with the questions actually shown (the stored question order is not the display order).
  questionId = null,
}) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  // What the recorder produces (WebM, or MP4 on iPhone / Safari) - the clip blobs are labelled with it.
  const recordedTypeRef = useRef("audio/webm");
  const questionNoRef = useRef(questionNo);
  const isUploadingRef = useRef(false);
  const inFlightRef = useRef(0);
  const isMountedRef = useRef(true);
  const intervalRef = useRef(null);

  const audioAction = MonitoringData?.result?.audio_action || "C";
  const uploadInterval = audioAction === "S" ? UPLOAD_INTERVAL_S_MODE : UPLOAD_INTERVAL_C_MODE;
  const alertVisibleRef = useRef(alertVisible);
  const audioCheckModeRef = useRef(audioCheckMode);
  const speechAlertRef = useRef(speechAlert);
  // Every question shown while the current clip was recording (a clip can span a question change).
  const questionIdRef = useRef(questionId);
  const clipQuestionIdsRef = useRef(new Set(questionId != null ? [String(questionId)] : []));
  // When the last speech popup appeared. A clip that ended before it was spoken before the
  // candidate saw that warning, so it doesn't raise another one (results can arrive up to a
  // minute late, and 3 popups for one burst of talking read as "still warned after I stopped").
  const speechAlertShownAtRef = useRef(0);

  // Keep refs in sync
  useEffect(() => {
    questionNoRef.current = questionNo;
  }, [questionNo]);

  useEffect(() => {
    alertVisibleRef.current = alertVisible;
  }, [alertVisible]);

  useEffect(() => {
    audioCheckModeRef.current = audioCheckMode;
  }, [audioCheckMode]);

  useEffect(() => {
    if (speechAlert && !speechAlertRef.current) speechAlertShownAtRef.current = Date.now();
    speechAlertRef.current = speechAlert;
  }, [speechAlert]);

  useEffect(() => {
    questionIdRef.current = questionId;
    if (questionId != null) clipQuestionIdsRef.current.add(String(questionId));
  }, [questionId]);

  const startRecording = useCallback(() => {
    if (!streamRef.current || !isMountedRef.current) return;

    try {
      // A format this browser records (MP4 on iPhone / Safari) - utils/audioRecordingType.js.
      const recorder = createAudioRecorder(streamRef.current);
      recordedTypeRef.current = recordedAudioType(recorder);

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onerror = (e) => {
        console.error("[BackgroundAudioRecorder] MediaRecorder error:", e.error);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(5000); // 5s timeslice for data collection
      console.log("[BackgroundAudioRecorder] Recording started");
    } catch (err) {
      console.error("[BackgroundAudioRecorder] Failed to start recording:", err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve();
        return;
      }

      recorder.onstop = () => {
        resolve();
      };

      recorder.stop();
    });
  }, []);

  const uploadAudio = useCallback(
    async (audioBlob, qNo, retryCount = 0, clip = {}) => {
      if (retryCount === 0 && inFlightRef.current >= MAX_IN_FLIGHT) {
        console.log(`[BackgroundAudioRecorder] ${inFlightRef.current} clips still being checked, not sending this one`);
        return;
      }
      inFlightRef.current += 1;
      let retry = false;
      try {
        const formData = new FormData();
        formData.append("audiofile", audioBlob, `proctoring_${examDetail[0].id}_q${qNo}_${Date.now()}.${audioFileExtension(audioBlob.type)}`);
        formData.append("userexamid", examDetail[0].id);
        formData.append("questionNo", qNo);
        if (clip.questionIds && clip.questionIds.length) formData.append("questionIds", clip.questionIds.join(","));
        formData.append("audioAction", audioAction);
        // Recording a video-interview answer: talking is the answer, so the backend skips the
        // talking check but still checks for a second voice. The mode is the one when the clip
        // ended - a slow upload must not carry a clip into a later mode.
        const mode = clip.mode || audioCheckModeRef.current;
        formData.append("talkingAllowed", mode === "speakersOnly" ? "true" : "false");

        console.log(`[BackgroundAudioRecorder] Uploading audio (${audioAction}) | question: ${qNo} | size: ${audioBlob.size}`);

        const resp = await axios({
          method: "POST",
          url: "/test/uploadProctoringAudio",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });

        if (!isMountedRef.current) return;

        console.log(`[BackgroundAudioRecorder] Result: speakers=${resp.data.speakers} | readingAloud=${resp.data.readingAloud} | mode=${audioAction}`);

        // Speech where talking isn't expected (the backend decides):
        // readingAloud = proven by the transcript -> warning, counted toward Speaker Warnings;
        // talking = not proven -> a reminder of the rule only. A proven result replaces an open reminder.
        const spokenBeforeLastWarning = clip.endedAt && clip.endedAt <= speechAlertShownAtRef.current;
        if (setSpeechAlert && !alertVisibleRef.current && !spokenBeforeLastWarning) {
          if (resp.data.readingAloud === true && speechAlertRef.current !== "readingAloud") {
            setSpeechAlert("readingAloud");
            if (setReadingAloudCount) setReadingAloudCount((prev) => prev + 1);
          } else if (resp.data.talking === true && !speechAlertRef.current && mode === "full") {
            setSpeechAlert("talking");
          }
        }

        // The backend confirms multiple speakers across clips (2 of the last 3) and says when
        // to warn in `alert`; one clip alone is too often a split voice or a noise. Older
        // backends without `alert` keep the previous rule (any clip with 2+ speakers).
        const shouldAlert = typeof resp.data.alert === "boolean" ? resp.data.alert : resp.data.speakers >= 2;
        if (shouldAlert) {
          if (alertVisibleRef.current) {
            console.log(`[BackgroundAudioRecorder] Multiple speakers confirmed but alert already visible, ignoring`);
            return;
          }
          console.log(`[BackgroundAudioRecorder] Multiple speakers confirmed! speakers: ${resp.data.speakers}`);
          setAudioCount((prev) => prev + 1);
          setAudioAlertShowModal(true);
        }
      } catch (error) {
        console.error("[BackgroundAudioRecorder] Upload failed:", error.message);
        // Retry only when the clip never reached the server (network error). A time-out or a
        // server error means the server has it or is still checking it - sending it again
        // doubled the speaker checks and put every later answer further behind.
        const notReceived = !error.response && error.code !== "ECONNABORTED" && error.code !== "ETIMEDOUT";
        retry = notReceived && retryCount < MAX_RETRIES && isMountedRef.current;
      } finally {
        inFlightRef.current -= 1;
      }
      if (retry) {
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
        console.log(`[BackgroundAudioRecorder] Retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          uploadAudio(audioBlob, qNo, retryCount + 1, clip);
        }, delay);
      }
    },
    [examDetail, audioAction, setAudioCount, setReadingAloudCount, setAudioAlertShowModal, setSpeechAlert]
  );

  const packageAndUpload = useCallback(
    async () => {
      // S mode: skip while alert popup is showing
      if (audioAction === "S" && (alertVisibleRef.current || speechAlertRef.current === "readingAloud")) {
        console.log("[BackgroundAudioRecorder] Alert visible, discarding chunks & skipping upload");
        chunksRef.current = [];
        return;
      }
      if (isUploadingRef.current) return;
      isUploadingRef.current = true;

      try {
        await stopRecording();

        if (audioCheckModeRef.current === "skip") {
          // Speaking/listening question: speaking is expected, nothing is checked or sent.
          chunksRef.current = [];
        } else if (chunksRef.current.length > 0) {
          const audioBlob = new Blob(chunksRef.current, { type: recordedTypeRef.current });
          chunksRef.current = [];
          uploadAudio(audioBlob, questionNoRef.current, 0, {
            questionIds: Array.from(clipQuestionIdsRef.current),
            endedAt: Date.now(),
            mode: audioCheckModeRef.current,
          });
        }

        clipQuestionIdsRef.current = new Set(questionIdRef.current != null ? [String(questionIdRef.current)] : []);

        if (isMountedRef.current && streamRef.current) {
          startRecording();
        }
      } finally {
        isUploadingRef.current = false;
      }
    },
    [audioAction, stopRecording, uploadAudio, startRecording]
  );

  // Initialize microphone on mount + start interval-based uploads
  useEffect(() => {
    isMountedRef.current = true;

    const initMic = async () => {
      try {
        // Phones: a copy of the exam's one microphone capture, shared with the video-answer
        // recorder so neither mutes the other. Desktops: an unprocessed capture, so a second voice
        // isn't suppressed (utils/sharedMic.js acquireCheckMicTrack).
        const stream = new MediaStream([await acquireCheckMicTrack()]);
        if (!isMountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          releaseSharedMicIfUnused();
          return;
        }
        streamRef.current = stream;
        startRecording();
        console.log(`[BackgroundAudioRecorder] Mic initialized | mode: ${audioAction} | interval: ${uploadInterval / 1000}s`);

        intervalRef.current = setInterval(() => {
          if (isMountedRef.current) {
            packageAndUpload();
          }
        }, uploadInterval);
      } catch (err) {
        console.error("[BackgroundAudioRecorder] Mic access denied:", err);
      }
    };

    initMic();

    return () => {
      isMountedRef.current = false;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      releaseSharedMicIfUnused();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On exam end (component unmount): upload final segment
  useEffect(() => {
    return () => {
      if (chunksRef.current.length > 0 && examDetail?.[0]?.id && audioCheckModeRef.current !== "skip") {
        const finalBlob = new Blob(chunksRef.current, { type: recordedTypeRef.current });
        chunksRef.current = [];

        const formData = new FormData();
        formData.append("audiofile", finalBlob, `proctoring_${examDetail[0].id}_final_${Date.now()}.${audioFileExtension(finalBlob.type)}`);
        formData.append("userexamid", examDetail[0].id);
        formData.append("questionNo", questionNoRef.current);
        formData.append("audioAction", audioAction);
        // Recording a video-interview answer: talking is the answer, so the backend skips the
        // talking check but still checks for a second voice.
        formData.append("talkingAllowed", audioCheckModeRef.current === "speakersOnly" ? "true" : "false");

        try {
          axios.post("/test/uploadProctoringAudio", formData);
        } catch (e) {
          // Best-effort on unmount
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default BackgroundAudioRecorder;
